// src/features/dashboard/pages/IdleTimeReport.tsx

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import DashboardHeader from '../../components/DashboardHeader';

import {
  ManagerDateProvider,
} from '../../context/ManagerDateContext';

import {
  useLoaderContext,
} from '../../../../components/Loader/LoaderContext';

import IdleReportHeader from '../../components/ManagerDashboard/IdleTimeReport/IdleReportHeader';

import IdleKpiCards from '../../components/ManagerDashboard/IdleTimeReport/IdleKpiCards';

import IdleFilters from '../../components/ManagerDashboard/IdleTimeReport/IdleFilters';

import IdleTeamChart from '../../components/ManagerDashboard/IdleTimeReport/IdleTeamChart';

import IdleBreakdownChart from '../../components/ManagerDashboard/IdleTimeReport/IdleBreakdownChart';

import DailyIdleTrendChart from '../../components/ManagerDashboard/IdleTimeReport/DailyIdleTrendChart';

import AssociateIdleDetailTable from '../../components/ManagerDashboard/IdleTimeReport/AssociateIdleDetailTable';

import useIdleTimeReport from '../../hooks/Manager/IdleTimeReport/useIdleTimeReport';

import type {
  IdleTeamChartResponse,
  IdleTrendResponse,
  AssociateIdleDetail,
} from '../../types/Manager/idleTime.types';


// =============================================================
// BROWSER ZOOM AUTO REFRESH
// =============================================================

/**
 * Chrome does not expose a reliable page-zoom percentage API.
 * However, changing the browser page zoom changes the CSS viewport
 * dimensions and triggers the window resize event.
 *
 * This hook watches the viewport dimensions after the page has loaded.
 * When they change, the page is reloaded once. This makes the dashboard
 * re-render with the correct responsive layout immediately after changing
 * Chrome zoom (80%, 90%, 100%, 110%, 125%, 150%, etc.).
 *
 * The debounce prevents multiple reloads while Chrome is still applying
 * the zoom level.
 */
const useBrowserZoomAutoRefresh = () => {
  const initialWidthRef = useRef<number>(window.innerWidth);
  const initialHeightRef = useRef<number>(window.innerHeight);
  const reloadStartedRef = useRef<boolean>(false);
  const resizeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    initialWidthRef.current = window.innerWidth;
    initialHeightRef.current = window.innerHeight;
    reloadStartedRef.current = false;

    const reloadPage = () => {
      if (reloadStartedRef.current) {
        return;
      }

      reloadStartedRef.current = true;

      // Give Chrome a short moment to finish applying the new zoom level
      // before reloading the application.
      window.setTimeout(() => {
        window.location.reload();
      }, 150);
    };

    const checkViewportChange = () => {
      if (reloadStartedRef.current) {
        return;
      }

      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;

      const widthChanged =
        currentWidth !== initialWidthRef.current;

      const heightChanged =
        currentHeight !== initialHeightRef.current;

      if (widthChanged || heightChanged) {
        reloadPage();
      }
    };

    const handleResize = () => {
      if (resizeTimerRef.current !== null) {
        window.clearTimeout(resizeTimerRef.current);
      }

      resizeTimerRef.current = window.setTimeout(() => {
        checkViewportChange();
      }, 250);
    };

    window.addEventListener(
      'resize',
      handleResize,
      { passive: true },
    );

    return () => {
      window.removeEventListener(
        'resize',
        handleResize,
      );

      if (resizeTimerRef.current !== null) {
        window.clearTimeout(
          resizeTimerRef.current,
        );
        resizeTimerRef.current = null;
      }
    };
  }, []);
};

// =============================================================
// CONTENT
// =============================================================

const IdleTimeReportContent = () => {

  // Automatically refresh the page when Chrome browser zoom changes.
  useBrowserZoomAutoRefresh();

  // ===========================================================
  // FILTER STATE
  // ===========================================================

  const [
    selectedTeam,
    setSelectedTeam,
  ] = useState<string>('');

  const [
    searchText,
    setSearchText,
  ] = useState<string>('');


  // ===========================================================
  // LOADER
  // ===========================================================

  const {
    hideLoader,
  } = useLoaderContext();


  // ===========================================================
  // API DATA
  // ===========================================================

  const {
    kpi,
    teamChart,
    breakdown,
    idleTrend,
    associateDetails,

    // Loading
    kpiLoading,
    teamChartLoading,
    breakdownLoading,
    trendLoading,
    associateLoading,

    // Errors
    kpiError,
    teamChartError,
    breakdownError,
    trendError,
    associateError,

  } = useIdleTimeReport();


  // ===========================================================
  // GLOBAL LOADER
  // ===========================================================

  useEffect(() => {

    const allLoaded =
      !kpiLoading &&
      !teamChartLoading &&
      !breakdownLoading &&
      !trendLoading &&
      !associateLoading;

    if (!allLoaded) {
      return;
    }

    const timer =
      window.setTimeout(() => {
        hideLoader();
      }, 300);

    return () => {
      window.clearTimeout(timer);
    };

  }, [
    kpiLoading,
    teamChartLoading,
    breakdownLoading,
    trendLoading,
    associateLoading,
    hideLoader,
  ]);


  // ===========================================================
  // CONTENT LOADING
  // ===========================================================

  const contentLoading =
    kpiLoading ||
    teamChartLoading ||
    breakdownLoading ||
    trendLoading ||
    associateLoading;


  // ===========================================================
  // NORMALIZED SELECTED TEAM
  // ===========================================================

  const normalizedSelectedTeam =
    selectedTeam
      .trim()
      .toLowerCase();


  // ===========================================================
  // SELECTED TEAM NAME
  // ===========================================================

  const normalizedSelectedTeamName =
    useMemo(() => {

      if (!normalizedSelectedTeam) {
        return '';
      }

      const selected =
        (teamChart ?? []).find(
          (item) =>
            String(
              item.teamLeadCode ?? '',
            )
              .trim()
              .toLowerCase() ===
            normalizedSelectedTeam,
        );

      return String(
        selected?.teamLeadName ?? '',
      )
        .trim()
        .toLowerCase();

    }, [
      teamChart,
      normalizedSelectedTeam,
    ]);


  // ===========================================================
  // FILTER TEAM CHART
  // ===========================================================

  const filteredTeamChart =
    useMemo(
      (): IdleTeamChartResponse[] => {

        if (!normalizedSelectedTeam) {
          return teamChart ?? [];
        }

        return (
          teamChart ?? []
        ).filter(
          (item) => {

            const teamCode =
              String(
                item.teamLeadCode ?? '',
              )
                .trim()
                .toLowerCase();

            return (
              teamCode ===
              normalizedSelectedTeam
            );
          },
        );

      },
      [
        teamChart,
        normalizedSelectedTeam,
      ],
    );


  // ===========================================================
  // FILTER DAILY TREND
  // ===========================================================

  const filteredIdleTrend =
    useMemo(
      (): IdleTrendResponse[] => {

        if (!normalizedSelectedTeam) {
          return idleTrend ?? [];
        }

        return (
          idleTrend ?? []
        ).filter(
          (item) => {

            const trendTeamCode =
              String(
                (
                  item as IdleTrendResponse & {
                    teamLeadCode?: string;
                  }
                ).teamLeadCode ?? '',
              )
                .trim()
                .toLowerCase();

            if (trendTeamCode) {
              return (
                trendTeamCode ===
                normalizedSelectedTeam
              );
            }

            const trendTeamName =
              String(
                item.teamLeadName ?? '',
              )
                .trim()
                .toLowerCase();

            return (
              !!normalizedSelectedTeamName &&
              trendTeamName ===
              normalizedSelectedTeamName
            );
          },
        );

      },
      [
        idleTrend,
        normalizedSelectedTeam,
        normalizedSelectedTeamName,
      ],
    );


  // ===========================================================
  // FILTER ASSOCIATE DETAILS
  // ===========================================================

  const filteredAssociateDetails =
    useMemo(
      (): AssociateIdleDetail[] => {

        const search =
          searchText
            .trim()
            .toLowerCase();

        return (
          associateDetails ?? []
        ).filter(
          (item) => {

            // -------------------------------------------------
            // TEAM FILTER
            // -------------------------------------------------

            let matchesTeam = true;

            const associate =
              item as AssociateIdleDetail & {
                teamLeadCode?: string;
                teamLeadName?: string;
              };

            const associateTeamCode =
              String(
                associate.teamLeadCode ?? '',
              )
                .trim()
                .toLowerCase();

            const associateTeamName =
              String(
                associate.teamLeadName ?? '',
              )
                .trim()
                .toLowerCase();

            if (
              normalizedSelectedTeam
            ) {

              if (
                associateTeamCode
              ) {

                matchesTeam =
                  associateTeamCode ===
                  normalizedSelectedTeam;

              } else if (
                associateTeamName
              ) {

                matchesTeam =
                  !!normalizedSelectedTeamName &&
                  associateTeamName ===
                  normalizedSelectedTeamName;

              } else {

                matchesTeam = false;

              }
            }


            // -------------------------------------------------
            // SEARCH FILTER
            // -------------------------------------------------

            const associateName =
              String(
                item.fullName ?? '',
              )
                .trim()
                .toLowerCase();

            const employeeCode =
              String(
                item.employeeCode ?? '',
              )
                .trim()
                .toLowerCase();

            const matchesSearch =
              !search ||
              associateName.includes(
                search,
              ) ||
              employeeCode.includes(
                search,
              );

            return (
              matchesTeam &&
              matchesSearch
            );
          },
        );

      },
      [
        associateDetails,
        searchText,
        normalizedSelectedTeam,
        normalizedSelectedTeamName,
      ],
    );


  // ===========================================================
  // BREAKDOWN
  // ===========================================================

  const filteredBreakdown =
    breakdown;


  // ===========================================================
  // TEAM CHANGE
  // ===========================================================

  const handleTeamChange = (
    teamName: string,
  ) => {

    const value =
      String(
        teamName ?? '',
      ).trim();

    setSelectedTeam(value);
  };


  // ===========================================================
  // SEARCH CHANGE
  // ===========================================================

  const handleSearchChange = (
    value: string,
  ) => {

    setSearchText(
      value ?? '',
    );
  };


  // ===========================================================
  // PAGE
  // ===========================================================

  return (

    <div
      className="
        box-border
        flex
        min-h-screen
        w-full
        max-w-full
        min-w-0
        flex-col
        overflow-x-hidden
        bg-[#F4F5F7]
        px-2
        pb-2
        sm:px-3
        sm:pb-3
        md:px-4
        md:pb-4
        lg:px-5
        lg:pb-5
      "
    >

      {/* =====================================================
          DASHBOARD HEADER
      ===================================================== */}

      <div
        className="
          box-border
          w-full
          max-w-full
          min-w-0
          shrink-0
        "
      >

        <DashboardHeader
          showBackButton
          breadcrumbs={[
            {
              label: 'Work Vista',
              path: '/',
            },
            {
              label:
                'Reporting Manager Dashboard',
             path: '/rm-dashboard',
            },
            {
              label:
                'Idle Time Report',
            },
          ]}
        />

      </div>


      {/* =====================================================
          MAIN REPORT

          relative is important because the local loader
          should cover this page only.
      ===================================================== */}

      <main
        className="
          relative
          -mt-[14px]
          box-border
          flex
          w-full
          max-w-full
          min-w-0
          flex-1
          flex-col
          overflow-x-hidden
          rounded-[16px]
          border
          border-[#D0D5DD]
          bg-[#F8F9FB]
          p-2
          sm:p-3
          md:p-4
          lg:p-5
        "
      >

        {/* ===================================================
            LOCAL LOADER
        =================================================== */}

        {contentLoading && (

          <div
            className="
              absolute
              inset-0
              z-[9999]
              flex
              items-center
              justify-center
              overflow-hidden
              rounded-[16px]
              bg-white/70
              px-4
              backdrop-blur-[1px]
            "
          >

            <div
              className="
                flex
                w-full
                max-w-[320px]
                flex-col
                items-center
                justify-center
                rounded-[12px]
                bg-white
                px-6
                py-6
                shadow-lg
                sm:px-8
              "
            >

              <div
                className="
                  h-[40px]
                  w-[40px]
                  shrink-0
                  animate-spin
                  rounded-full
                  border-[4px]
                  border-[#E4E7EC]
                  border-t-[#2563EB]
                "
              />

              <p
                className="
                  mt-[12px]
                  text-center
                  text-[13px]
                  font-medium
                  text-[#475467]
                "
              >
                Loading Idle Time...
              </p>

              <p
                className="
                  mt-[4px]
                  text-center
                  text-[11px]
                  text-[#98A2B3]
                "
              >
                Fetching idle time data
              </p>

            </div>

          </div>

        )}


        {/* ===================================================
            REPORT HEADER
        =================================================== */}

        <section
          className="
            box-border
            block
            w-full
            max-w-full
            min-w-0
          "
        >

          <IdleReportHeader
            kpi={kpi}
          />

        </section>


        {/* ===================================================
            KPI CARDS
        =================================================== */}

        <section
          className="
            mt-4
            box-border
            block
            w-full
            max-w-full
            min-w-0
            sm:mt-5
          "
        >

          {kpiError ? (

            <div
              className="
                box-border
                w-full
                max-w-full
                rounded-[12px]
                border
                border-red-200
                bg-white
                p-4
                text-sm
                text-red-500
              "
            >
              {kpiError}
            </div>

          ) : (

            <div
              className="
                box-border
                w-full
                max-w-full
                min-w-0
              "
            >

              <IdleKpiCards
                data={kpi}
              />

            </div>

          )}

        </section>


        {/* ===================================================
            FILTERS
        =================================================== */}

        <section
          className="
            mt-4
            box-border
            block
            w-full
            max-w-full
            min-w-0
            sm:mt-5
          "
        >

          <IdleFilters
            teamLeaders={
              teamChart
            }
            selectedTeam={
              selectedTeam
            }
            onTeamChange={
              handleTeamChange
            }
            searchText={
              searchText
            }
            onSearchChange={
              handleSearchChange
            }
          />

        </section>


        {/* ===================================================
            TEAM + BREAKDOWN

            IMPORTANT RESPONSIVE BEHAVIOR:

            Small:
            Team
            Breakdown

            Medium/Desktop:
            Team             Breakdown
            ┌────────────┐  ┌────────────┐
            │            │  │            │
            │   Chart    │  │   Donut    │
            │            │  │            │
            └────────────┘  └────────────┘

            Both columns are equal width and use the
            complete available row.
        =================================================== */}

        <section
          className="
            mt-4
            grid
            w-full
            max-w-full
            min-w-0
            grid-cols-1
            gap-4
            sm:mt-5
            sm:gap-5
            lg:grid-cols-2
            lg:items-stretch
            lg:gap-5
            xl:gap-6
          "
        >

          {/* =================================================
              TEAM CHART
          ================================================= */}

          <div
            className="
              box-border
              flex
              w-full
              max-w-full
              min-w-0
              flex-col
            "
          >

            {teamChartLoading ? (

              <div
                className="
                  box-border
                  flex
                  min-h-[320px]
                  w-full
                  max-w-full
                  min-w-0
                  flex-1
                  items-center
                  justify-center
                  overflow-hidden
                  rounded-[12px]
                  border
                  border-[#D0D5DD]
                  bg-white
                  px-4
                  py-6
                  sm:min-h-[340px]
                  md:min-h-[360px]
                "
              >

                <span
                  className="
                    text-center
                    text-xs
                    text-slate-500
                    sm:text-sm
                  "
                >
                  Loading Team Chart...
                </span>

              </div>

            ) : teamChartError ? (

              <div
                className="
                  box-border
                  flex
                  min-h-[320px]
                  w-full
                  max-w-full
                  min-w-0
                  flex-1
                  items-center
                  justify-center
                  overflow-hidden
                  rounded-[12px]
                  border
                  border-red-200
                  bg-red-50
                  px-4
                  py-6
                  text-center
                  text-xs
                  text-red-600
                  sm:min-h-[340px]
                  sm:text-sm
                  md:min-h-[360px]
                "
              >
                {teamChartError}
              </div>

            ) : (

              <div
                className="
                  box-border
                  flex
                  h-full
                  w-full
                  max-w-full
                  min-w-0
                  flex-1
                  overflow-hidden
                "
              >

                <IdleTeamChart
                  data={
                    filteredTeamChart
                  }
                />

              </div>

            )}

          </div>


          {/* =================================================
              BREAKDOWN CHART
          ================================================= */}

          <div
            className="
              box-border
              flex
              w-full
              max-w-full
              min-w-0
              flex-col
            "
          >

            {breakdownLoading ? (

              <div
                className="
                  box-border
                  flex
                  min-h-[320px]
                  w-full
                  max-w-full
                  min-w-0
                  flex-1
                  items-center
                  justify-center
                  overflow-hidden
                  rounded-[12px]
                  border
                  border-[#D0D5DD]
                  bg-white
                  px-4
                  py-6
                  sm:min-h-[340px]
                  md:min-h-[360px]
                "
              >

                <span
                  className="
                    text-center
                    text-xs
                    text-slate-500
                    sm:text-sm
                  "
                >
                  Loading Breakdown...
                </span>

              </div>

            ) : breakdownError ? (

              <div
                className="
                  box-border
                  flex
                  min-h-[320px]
                  w-full
                  max-w-full
                  min-w-0
                  flex-1
                  items-center
                  justify-center
                  overflow-hidden
                  rounded-[12px]
                  border
                  border-red-200
                  bg-red-50
                  px-4
                  py-6
                  text-center
                  text-xs
                  text-red-600
                  sm:min-h-[340px]
                  sm:text-sm
                  md:min-h-[360px]
                "
              >
                {breakdownError}
              </div>

            ) : (

              <div
                className="
                  box-border
                  flex
                  h-full
                  w-full
                  max-w-full
                  min-w-0
                  flex-1
                  overflow-hidden
                "
              >

                <IdleBreakdownChart
                  data={
                    filteredBreakdown
                  }
                />

              </div>

            )}

          </div>

        </section>


        {/* ===================================================
            DAILY IDLE TREND
        =================================================== */}

        <section
          className="
            mt-4
            box-border
            block
            w-full
            max-w-full
            min-w-0
            sm:mt-5
          "
        >

          {trendLoading ? (

            <div
              className="
                box-border
                flex
                min-h-[320px]
                w-full
                max-w-full
                min-w-0
                items-center
                justify-center
                overflow-hidden
                rounded-[12px]
                border
                border-[#D0D5DD]
                bg-white
                px-4
                py-6
                sm:min-h-[340px]
                md:min-h-[360px]
              "
            >

              <span
                className="
                  text-center
                  text-xs
                  text-slate-500
                  sm:text-sm
                "
              >
                Loading Daily Idle Trend...
              </span>

            </div>

          ) : trendError ? (

            <div
              className="
                box-border
                flex
                min-h-[320px]
                w-full
                max-w-full
                min-w-0
                items-center
                justify-center
                overflow-hidden
                rounded-[12px]
                border
                border-red-200
                bg-red-50
                px-4
                py-6
                text-center
                text-xs
                text-red-600
                sm:min-h-[340px]
                sm:text-sm
                md:min-h-[360px]
              "
            >
              {trendError}
            </div>

          ) : (

            <div
              className="
                box-border
                block
                w-full
                max-w-full
                min-w-0
                overflow-hidden
              "
            >

              <DailyIdleTrendChart
                data={
                  filteredIdleTrend
                }
              />

            </div>

          )}

        </section>


        {/* ===================================================
            ASSOCIATE DETAILS
        =================================================== */}

        <section
          className="
            mt-4
            box-border
            block
            w-full
            max-w-full
            min-w-0
            sm:mt-5
          "
        >

          {associateLoading ? (

            <div
              className="
                box-border
                flex
                min-h-[320px]
                w-full
                max-w-full
                min-w-0
                items-center
                justify-center
                overflow-hidden
                rounded-[12px]
                border
                border-[#D0D5DD]
                bg-white
                px-4
                py-6
                sm:min-h-[360px]
                md:min-h-[380px]
              "
            >

              <span
                className="
                  text-center
                  text-xs
                  text-slate-500
                  sm:text-sm
                "
              >
                Loading Associate Details...
              </span>

            </div>

          ) : associateError ? (

            <div
              className="
                box-border
                flex
                min-h-[320px]
                w-full
                max-w-full
                min-w-0
                items-center
                justify-center
                overflow-hidden
                rounded-[12px]
                border
                border-red-200
                bg-red-50
                px-4
                py-6
                text-center
                text-xs
                text-red-600
                sm:min-h-[360px]
                sm:text-sm
                md:min-h-[380px]
              "
            >
              {associateError}
            </div>

          ) : (

            /*
             * Only the table is allowed to scroll horizontally.
             * The entire page never gets a horizontal scrollbar.
             */

            <div
              className="
                box-border
                block
                w-full
                max-w-full
                min-w-0
                overflow-x-auto
                overflow-y-hidden
              "
            >

              <AssociateIdleDetailTable
                data={
                  filteredAssociateDetails
                }
              />

            </div>

          )}

        </section>

      </main>

    </div>
  );
};


// =============================================================
// PAGE PROVIDER
// =============================================================

const IdleTimeReport = () => {

  return (

    <ManagerDateProvider>

      <IdleTimeReportContent />

    </ManagerDateProvider>
  );
};


export default IdleTimeReport;